import { useCallback, useEffect, useMemo, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getLiveEventsMap } from "../api/heat-map.api";
import type { LiveEventAreaModel } from "../types/LiveEvent";
import type { DashboardFilterPayload } from "../utils/heat-map.utils";
import { toActivityLevels, toEventDetails } from "../utils/heat-map.utils";

export const useLiveEvents = (payload: DashboardFilterPayload) => {
  const [data, setData] = useState<LiveEventAreaModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await runLatestRequest((signal) => getLiveEventsMap(payload, signal));

      if (!result) return undefined;

      setData(result);
      return result;
    } catch (fetchError) {
      setData(null);
      setError(getApiErrorMessage(fetchError, "Failed to load live events"));
      return undefined;
    }
  }, [payload, runLatestRequest]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const activityLevels = useMemo(() => (data ? toActivityLevels(data) : null), [data]);
  const eventDetails = useMemo(() => (data ? toEventDetails(data) : null), [data]);

  return { data, activityLevels, eventDetails, loading, error, refetch: fetchData };
};
